import React, { useEffect, useRef, useState } from 'react';
import { Users, Globe, Heart, Target, CheckCircle, Calendar, Waves, Shield, HandHeart, BookOpen, Phone, FolderOpen, Compass, Link } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { useNavigate } from 'react-router-dom';

const Memberships = () => {
  const navigate = useNavigate();
  const [visibleSections, setVisibleSections] = useState(new Set());
  const sectionRefs = useRef({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections(prev => new Set([...prev, entry.target.id]));
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    // Observe all sections
    Object.values(sectionRefs.current).forEach(ref => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  const membershipBenefits = [
    {
      icon: Users,
      title: "Group Sessions",
      description: "Shared learning and accountability through structured group experiences that foster connection and mutual support."
    },
    {
      icon: Target,
      title: "Resilience Practices",
      description: "Evidence-based activities and practices designed to build confidence, emotional strength, and personal resilience."
    },
    {
      icon: Globe,
      title: "Immersive Retreats",
      description: "Powerful breakthrough experiences through focused, real-world retreats that create lasting transformation."
    }
  ];

  const programFeatures = [
    {
      icon: Heart,
      title: "Individual Coaching",
      description: "Personalized one-on-one support tailored to your specific needs and goals during transitions."
    },
    {
      icon: Users,
      title: "Mentorship",
      description: "Guidance from experienced professionals who understand the challenges of personal and professional growth."
    },
    {
      icon: Globe,
      title: "Peer Connection",
      description: "Connect with a global network of women committed to progress and mutual support."
    }
  ];

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Hero Section */}
      <section 
        id="hero"
        ref={el => sectionRefs.current.hero = el}
        className={`py-20 bg-stone-50 transition-all duration-1500 delay-200 ${
          visibleSections.has('hero') 
            ? 'opacity-100 translate-y-0' 
            : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              International Women's Connection
            </h1>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              It's a space where growth is supported by structure and shared by a global network of women committed to progress. The International Women's Connection (IWC) is a membership program designed to support women through personal and professional transitions with a combination of individual coaching, mentorship, and peer connection.
            </p>
          </div>
        </div>
      </section>

      {/* Membership Benefits Section */}
      <section 
        id="benefits"
        ref={el => sectionRefs.current.benefits = el}
        className={`py-20 bg-stone-50 transition-all duration-1500 delay-200 ${
          visibleSections.has('benefits') 
            ? 'opacity-100 translate-y-0' 
            : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              What Members Access
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A comprehensive support system designed to empower your journey through structured learning, practical tools, and transformative experiences.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {membershipBenefits.map((benefit, index) => (
              <div key={index} className="text-center space-y-6">
                <div className="relative mx-auto w-32 h-32">
                  <img 
                    src={
                      benefit.title === "Group Sessions" 
                        ? "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwzfHx3b21lbiUyMHRhbGtpbmclMjB0b2dldGhlcnxlbnwwfHx8fDE3NTk1MTA1MDh8MA&ixlib=rb-4.1.0&q=85"
                        : benefit.title === "Resilience Practices"
                        ? "/practices.jpg"
                        : "/island.png"
                    }
                    alt={benefit.title}
                    className="w-full h-full object-cover rounded-full shadow-lg hover:shadow-xl transition-shadow duration-300"
                  />
                </div>
                <div className="space-y-3">
                  <h3 className="text-2xl font-bold text-gray-900">{benefit.title}</h3>
                  <p className="text-gray-600 leading-relaxed max-w-sm mx-auto">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Program Features Section */}
      <section 
        id="features"
        ref={el => sectionRefs.current.features = el}
        className={`py-20 bg-stone-50 transition-all duration-1500 delay-200 ${
          visibleSections.has('features') 
            ? 'opacity-100 translate-y-0' 
            : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Comprehensive Support System
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our membership combines multiple approaches to ensure you have the support you need during life's most important transitions.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {programFeatures.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <Card key={index} className="text-center p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                  <CardContent className="space-y-6">
                    <div className="flex justify-center">
                      <div 
                        className="p-4 rounded-full"
                        style={{ backgroundColor: 'rgba(127, 81, 244, 0.1)' }}
                      >
                        <IconComponent className="h-8 w-8" style={{ color: 'rgba(127, 81, 244, 1)' }} />
                      </div>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900">{feature.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>


      {/* CTA Section */}
      <section 
        id="cta"
        ref={el => sectionRefs.current.cta = el}
        className={`bg-stone-50 transition-all duration-1500 delay-200 ${
          visibleSections.has('cta') 
            ? 'opacity-100 translate-y-0' 
            : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="w-full h-px bg-black"></div>
        <div className="py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Ready to Join Our <span className="relative inline-block">
                <span className="relative z-10">Global Community</span>
                <span className="absolute inset-0 bg-gradient-to-r from-blue-200 to-purple-200 opacity-30 rounded-lg transform -rotate-1"></span>
                <span className="absolute inset-0 bg-gradient-to-r from-blue-100 to-purple-100 opacity-50 rounded-lg transform rotate-1"></span>
              </span>?
            </h2>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Connect with women worldwide who are committed to growth, progress, and supporting each other through life's transitions.
            </p>
            <button 
              onClick={() => {
                navigate('/contact');
                window.scrollTo(0, 0);
              }}
              className="px-4 py-2 border rounded-full text-sm transition-colors duration-200" 
              style={{ 
                borderColor: '#6a6acb', 
                color: '#6a6acb',
                backgroundColor: 'transparent'
              }} 
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#6a6acb';
                e.target.style.color = 'white';
              }} 
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = '#6a6acb';
              }}
            >
              Join IWC Today →
            </button>
          </div>
        </div>
        <div className="w-full h-px bg-black"></div>
      </section>
    </div>
  );
};

export default Memberships;
